import _ from 'lodash';
import React from 'react';

import { useLocation } from 'react-router';

import { REGISSEUR } from '@/constants';
import { estEncaissementDirect } from '@/utils';
import { useRegieStore, useRegisseurStore } from '@/zustand';

import ModalArreteComptable from './ModalArreteComptable';

type ArreteComptableGuardProps = {
  children: React.ReactNode;
};

const getSessionStorageKey = (identifiant: number | string) => `arrete-seen-${identifiant}`;

/**
 * ArreteComptableGuard
 *
 * Guards the application flow to enforce arrêté comptable requirement.
 * Displays a modal when dateArrete is set on the régie,
 * except for agents de caisse, when on the versements/controle-bordereaux page,
 * or when the modal has already been shown during this session.
 *
 * Must be placed after RegisseurContextProvider so the regisseur is connected
 * and mandat is available for permission checks.
 */
const ArreteComptableGuard = (props: ArreteComptableGuardProps) => {
  const { children } = props;

  const location = useLocation();

  const regie = useRegieStore((state) => state.regie);
  const { dateArrete, arreteAnnuelRequis } = regie || {};

  const connectedRegisseur = useRegisseurStore((state) => state.regisseur);
  const { mandat } = connectedRegisseur || {};

  const isAgent = mandat === REGISSEUR.TYPE_AGENT;

  // En mode embedded (encaissement direct), on n'affiche pas la modale d'arrêté périodique :
  // l'utilisateur traite un seul encaissement et n'a pas à voir ce rappel.
  // Deux signaux combinés (OR) pour fiabilité : la route est la source de vérité réactive
  // (indépendante du timing et des URL params) ; le sessionStorage couvre les cas où le
  // composant est monté avant que la redirection vers /encaissement-direct ait pris effet.
  const isEmbedded = React.useMemo(
    () => estEncaissementDirect() || location.pathname.includes('/encaissement-direct'),
    [location.pathname],
  );

  const [dismissed, setDismissed] = React.useState(() => {
    if (regie?.identifiant) {
      return sessionStorage.getItem(getSessionStorageKey(regie.identifiant)) === 'true';
    }
    return false;
  });

  // Reset dismissed quand la régie change
  React.useEffect(() => {
    if (regie?.identifiant) {
      const key = getSessionStorageKey(regie.identifiant);
      setDismissed(sessionStorage.getItem(key) === 'true');
    } else {
      setDismissed(false);
    }
  }, [regie?.identifiant]);

  // Pattern inspiré de RegisseurRouter.tsx lignes 36-46
  const isOnExceptionPage = React.useMemo(() => {
    let locationRoot = location.pathname;
    locationRoot = _.replace(locationRoot, '/administres/regie', '');
    locationRoot = _.replace(locationRoot, '/finances/regie', '');
    locationRoot = _.replace(locationRoot, '/facturation/regie', '');

    return locationRoot.includes('/versements-remises/controle-bordereaux');
  }, [location.pathname]);

  // Afficher modal si dateArrete présent ET pas sur page exception
  // ET pas agent de caisse ET pas déjà vu dans cette session
  // La procédure d'arrêté annuel de rattrapage (ArreteAnnuelProcedureCreator) prime : on n'affiche
  // pas le rappel soft d'arrêté périodique pour ne pas faire concurrence à la procédure obligatoire.
  const showModal = React.useMemo(
    () =>
      !_.isEmpty(dateArrete) &&
      !arreteAnnuelRequis &&
      !isOnExceptionPage &&
      !isAgent &&
      !dismissed &&
      !isEmbedded,
    [dateArrete, arreteAnnuelRequis, isOnExceptionPage, isAgent, dismissed, isEmbedded],
  );

  // Marquer comme vu dès l'affichage de la modal
  React.useEffect(() => {
    if (showModal && regie?.identifiant) {
      sessionStorage.setItem(getSessionStorageKey(regie.identifiant), 'true');
    }
  }, [showModal, regie?.identifiant]);

  const handleDismiss = React.useCallback(() => {
    setDismissed(true);
  }, []);

  if (showModal) {
    return (
      <ModalArreteComptable
        onDismiss={handleDismiss}
        hasReglements={false} // {regie?.nombreReglementsAVerser > 0}
      />
    );
  }

  return children;
};

export default ArreteComptableGuard;
